import React, { useCallback } from 'react';
import {
  Layers,
  BarChart3,
  Crosshair,
  Magnet,
  Key,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Grid,
} from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { ViewMode } from '../../../core/types/animation.types';
import { cn } from '../../utils/cn';

interface AnimationToolbarProps {
  className?: string;
}

interface ViewModeButton {
  mode: ViewMode;
  label: string;
  icon: React.ReactNode;
}

const VIEW_MODE_BUTTONS: ViewModeButton[] = [
  { mode: 'timeline', label: 'Timeline', icon: <Layers size={14} /> },
  { mode: 'dopesheet', label: 'Dope Sheet', icon: <Grid size={14} /> },
  { mode: 'curves', label: 'Curves', icon: <BarChart3 size={14} /> },
];

export const AnimationToolbar: React.FC<AnimationToolbarProps> = ({ className }) => {
  const {
    viewMode,
    snapToFrames,
    autoKeyframe,
    showGrid,
    viewport,
    setViewMode,
    toggleSnapToFrames,
    toggleAutoKeyframe,
    toggleGrid,
    zoomIn,
    zoomOut,
    fitToView,
  } = useAnimationStore();

  const { pixelsPerSecond } = viewport;

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
    },
    [setViewMode]
  );

  // Handle snap toggle
  const handleSnapToggle = useCallback(() => {
    toggleSnapToFrames();
  }, [toggleSnapToFrames]);

  // Handle auto-keyframe toggle
  const handleAutoKeyframeToggle = useCallback(() => {
    toggleAutoKeyframe();
  }, [toggleAutoKeyframe]);

  // Handle grid toggle
  const handleGridToggle = useCallback(() => {
    toggleGrid();
  }, [toggleGrid]);

  // Handle zoom
  const handleZoomIn = useCallback(() => {
    zoomIn();
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut();
  }, [zoomOut]);

  const handleFitToView = useCallback(() => {
    fitToView();
  }, [fitToView]);

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 bg-zinc-800 border-b border-zinc-700',
        className
      )}
    >
      {/* View Mode Toggle */}
      <div className="flex items-center bg-zinc-900 rounded overflow-hidden">
        {VIEW_MODE_BUTTONS.map((button) => (
          <button
            key={button.mode}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs transition-colors',
              viewMode === button.mode
                ? 'bg-blue-600 text-white'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
            )}
            onClick={() => handleViewModeChange(button.mode)}
            title={button.label}
          >
            {button.icon}
            <span className="hidden sm:inline">{button.label}</span>
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="w-px h-5 bg-zinc-700" />

      {/* Snap to Frames */}
      <button
        className={cn(
          'p-1.5 rounded transition-colors',
          snapToFrames
            ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
            : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
        )}
        onClick={handleSnapToggle}
        title={snapToFrames ? 'Snap to Frames: On' : 'Snap to Frames: Off'}
      >
        <Magnet size={16} />
      </button>

      {/* Auto-Keyframe */}
      <button
        className={cn(
          'p-1.5 rounded transition-colors',
          autoKeyframe
            ? 'text-red-400 bg-red-500/20 hover:bg-red-500/30'
            : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
        )}
        onClick={handleAutoKeyframeToggle}
        title={autoKeyframe ? 'Auto-Keyframe: On' : 'Auto-Keyframe: Off'}
      >
        <Key size={16} />
      </button>

      {/* Grid Toggle */}
      <button
        className={cn(
          'p-1.5 rounded transition-colors',
          showGrid
            ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
            : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
        )}
        onClick={handleGridToggle}
        title={showGrid ? 'Grid: On' : 'Grid: Off'}
      >
        <Crosshair size={16} />
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Zoom Controls */}
      <div className="flex items-center gap-1">
        <button
          className="p-1.5 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <ZoomOut size={16} />
        </button>

        <div className="px-2 py-0.5 min-w-[70px] text-center text-xs text-zinc-400 bg-zinc-900 rounded">
          {Math.round(pixelsPerSecond)} px/s
        </div>

        <button
          className="p-1.5 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
          onClick={handleZoomIn}
          title="Zoom In"
        >
          <ZoomIn size={16} />
        </button>

        <button
          className="p-1.5 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
          onClick={handleFitToView}
          title="Fit to View"
        >
          <Maximize2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default AnimationToolbar;
